/* Marketim catalogue extensions. Dawn owns the cart, product form and facets. */
(() => {
  if (customElements.get('mk-favorite')) return;

  const storageKey = 'mk_favs';
  const isHandle = (handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(handle);
  let favorites = [];
  let memoryOnly = false;

  function readFavorites() {
    if (memoryOnly) return [...favorites];
    let value;
    try {
      value = window.localStorage.getItem(storageKey);
    } catch {
      memoryOnly = true;
      return [...favorites];
    }
    try {
      const handles = JSON.parse(value || '[]');
      return Array.isArray(handles) ? [...new Set(handles.filter(isHandle))].slice(-100) : [];
    } catch {
      return [];
    }
  }

  favorites = readFavorites();

  function sync(root = document) {
    const update = (element) => {
      if (element.matches('[data-mk-fav-count]')) {
        const count = String(favorites.length);
        if (element.textContent !== count) element.textContent = count;
        element.hidden = favorites.length === 0;
      } else {
        element.setAttribute('aria-pressed', String(favorites.includes(element.dataset.mkFavHandle)));
      }
    };
    const selector = '[data-mk-fav-count], button[data-mk-fav-handle]';
    if (root.matches?.(selector)) update(root);
    root.querySelectorAll(selector).forEach(update);
  }

  function changeFavorite(handle, removeOnly) {
    if (!isHandle(handle)) return;
    const handles = readFavorites();
    favorites = handles.includes(handle) || removeOnly
      ? handles.filter((value) => value !== handle) : [...handles, handle].slice(-100);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(favorites));
    } catch {
      // Private browsing / quota failures still allow favorites for this visit.
      memoryOnly = true;
    }
    window.dispatchEvent(new CustomEvent('mk:favs-changed', { detail: { handles: [...favorites] } }));
  }

  customElements.define('mk-favorite', class extends HTMLElement {
    connectedCallback() {
      sync(this);
      this.hidden = false;
    }
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('button[data-mk-fav-handle]');
    if (!button || button.disabled) return;
    event.preventDefault();
    changeFavorite(button.dataset.mkFavHandle, button.hasAttribute('data-mk-fav-remove'));
  });
  window.addEventListener('mk:favs-changed', () => {
    favorites = readFavorites();
    sync();
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey && event.key !== null) return;
    favorites = readFavorites();
    window.dispatchEvent(new CustomEvent('mk:favs-changed', { detail: { handles: [...favorites] } }));
  });
  window.addEventListener('pageshow', () => {
    favorites = readFavorites();
    window.dispatchEvent(new CustomEvent('mk:favs-changed', { detail: { handles: [...favorites] } }));
  });
  // Dawn replaces facet grids and header fragments without a full page load.
  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) sync(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
  sync();

  const productRequests = new Map();
  const queue = [];
  let activeRequests = 0;

  function pumpRequests() {
    while (activeRequests < 24 && queue.length) {
      const { url, resolve, reject } = queue.shift();
      activeRequests += 1;
      (async () => {
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Product unavailable');
          const product = await response.json();
          if (typeof product.title !== 'string' || !Array.isArray(product.variants)) throw new Error('Invalid product');
          resolve(product);
        } catch (error) {
          productRequests.delete(url);
          reject(error);
        } finally {
          activeRequests -= 1;
          pumpRequests();
        }
      })();
    }
  }

  function getProduct(handle, root) {
    const url = `${root}products/${encodeURIComponent(handle)}.js`;
    if (!productRequests.has(url)) {
      productRequests.set(url, new Promise((resolve, reject) => queue.push({ url, resolve, reject })));
      pumpRequests();
    }
    return productRequests.get(url);
  }

  function formatMoney(cents, format, currency) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    const formats = {
      amount: ['en-US', 2],
      amount_no_decimals: ['en-US', 0],
      amount_with_comma_separator: ['de-DE', 2],
      amount_no_decimals_with_comma_separator: ['de-DE', 0],
      amount_with_apostrophe_separator: ['de-CH', 2],
      amount_with_space_separator: ['fr-FR', 2],
      amount_no_decimals_with_space_separator: ['fr-FR', 0],
      amount_with_period_and_space_separator: ['en-US', 2],
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match || !formats[match[1]]) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(value / 100);
    }
    const [locale, decimals] = formats[match[1]];
    let amount = (value / 100).toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    if (match[1] === 'amount_with_period_and_space_separator') amount = amount.replace(/,/g, ' ');
    return format.replace(match[0], amount);
  }

  customElements.define('mk-favorites', class extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-mk-fav-grid]');
      this.template = this.querySelector('template');
      this.loading = this.querySelector('[data-mk-fav-loading]');
      this.empty = this.querySelector('[data-mk-fav-empty]');
      this.error = this.querySelector('[data-mk-fav-error]');
      this.retry = this.querySelector('[data-mk-fav-retry]');
      this.heading = this.querySelector('h1');
      this.root = `${this.dataset.root.replace(/\/$/, '')}/`;
      this.onChange = () => this.refresh();
      window.addEventListener('mk:favs-changed', this.onChange);
      this.retry.addEventListener('click', this.onChange);
      this.refresh();
    }

    disconnectedCallback() {
      this.version = (this.version || 0) + 1;
      window.removeEventListener('mk:favs-changed', this.onChange);
      this.retry?.removeEventListener('click', this.onChange);
    }

    async refresh() {
      const version = this.version = (this.version || 0) + 1;
      const handles = [...favorites];
      this.loading.hidden = handles.length === 0;
      this.grid.setAttribute('aria-busy', String(handles.length > 0));
      this.empty.hidden = handles.length > 0;
      this.error.hidden = true;
      const results = await Promise.allSettled(handles.map((handle) => getProduct(handle, this.root)));
      if (!this.isConnected || this.version !== version) return;

      const products = [];
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled') return;
        const product = result.value;
        const variant = product.variants.find((item) => item.available && Number.isSafeInteger(item.id) && item.id > 0);
        products.push({ handle: handles[index], product, variant });
      });
      products.sort((a, b) => Number(Boolean(b.variant)) - Number(Boolean(a.variant)));
      this.renderCards(products);
      this.grid.hidden = products.length === 0;
      this.grid.setAttribute('aria-busy', 'false');
      this.loading.hidden = true;
      this.error.hidden = !results.some((result) => result.status === 'rejected');
    }

    renderCards(products) {
      const previous = [...this.grid.children];
      const focusedCard = document.activeElement?.closest('[data-mk-fav-item]');
      const focusIndex = previous.indexOf(focusedCard);
      const existing = new Map(previous.map((card) => [card.dataset.mkFavItem, card]));
      const kept = new Set(products.map(({ handle }) => handle));
      previous.forEach((card) => { if (!kept.has(card.dataset.mkFavItem)) card.remove(); });
      products.forEach((item, index) => {
        const card = existing.get(item.handle) || this.createCard(item);
        if (this.grid.children[index] !== card) this.grid.insertBefore(card, this.grid.children[index] || null);
      });
      if (focusIndex >= 0 && !focusedCard.isConnected) {
        const next = this.grid.children[Math.min(focusIndex, products.length - 1)];
        (next?.querySelector('[data-mk-fav-remove]') || this.heading).focus();
      }
    }

    createCard({ handle, product, variant }) {
      const card = this.template.content.firstElementChild.cloneNode(true);
      card.dataset.mkFavItem = handle;
      card.classList.toggle('mk-card--sold-out', !variant);
      card.querySelectorAll('[data-mk-fav-link]').forEach((link) => { link.href = `${this.root}products/${encodeURIComponent(handle)}`; });
      card.querySelector('[data-mk-fav-title]').textContent = product.title;
      card.querySelector('[data-mk-fav-image-link]').setAttribute('aria-label', product.title);
      card.querySelector('[data-mk-fav-price]').textContent = formatMoney(variant?.price ?? product.price, this.dataset.moneyFormat, this.dataset.currency);
      card.querySelector('[data-mk-fav-sold-out]').hidden = Boolean(variant);
      const remove = card.querySelector('[data-mk-fav-handle]');
      remove.dataset.mkFavHandle = handle;
      remove.setAttribute('aria-label', `${remove.textContent.trim()}: ${product.title}`);
      const button = card.querySelector('[type="submit"]');
      button.disabled = !variant;
      button.setAttribute('aria-label', `${button.textContent.trim()}: ${product.title}`);
      card.querySelector('[name="id"]').value = variant?.id || '';
      card.querySelector('form').action = window.routes.cart_add_url;
      const image = card.querySelector('[data-mk-fav-image]');
      const source = product.featured_image || product.images?.[0];
      try {
        if (typeof source !== 'string') throw new Error('No image');
        const url = new URL(source, window.location.origin);
        if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid image');
        const imageUrl = (width) => {
          const sized = new URL(url);
          sized.searchParams.set('width', width);
          return sized.href;
        };
        image.src = imageUrl(533);
        image.srcset = [165, 360, 533, 720].map((width) => `${imageUrl(width)} ${width}w`).join(', ');
        image.alt = product.title;
        image.hidden = false;
        card.querySelector('[data-mk-fav-placeholder]').hidden = true;
      } catch {
        image.remove();
      }
      return card;
    }
  });
})();

if (!customElements.get('mk-quick-add')) {
  customElements.define('mk-quick-add', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.status = this.querySelector('[role="status"]');
      this.onSubmit = this.submit.bind(this);
      this.form?.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || this.button.disabled) return;
      this.busy = true;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.setAttribute('aria-busy', 'true');
      this.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.status.hidden = true;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const data = new FormData(this.form);
      let errorMessage = this.dataset.errorMessage;
      const sectionIds = cart ? cart.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'];
      data.append('sections', sectionIds.join(','));
      data.append('sections_url', window.location.pathname);
      cart?.setActiveElement(this.button);

      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        const response = await fetch(routes.cart_add_url, { ...config, body: data });
        const result = await response.json();
        if (!response.ok || result.status) {
          const message = typeof result.description === 'string' ? result.description : this.dataset.errorMessage;
          errorMessage = message;
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'mk-quick-add', productVariantId: data.get('id'),
            errors: result.errors || message, message,
          });
          throw new Error(message);
        }

        // The add succeeded. Never offer a retry for a section-render failure:
        // going to the real cart avoids accidentally adding the same item twice.
        try {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mk-quick-add', productVariantId: data.get('id'), cartData: result,
          });
          if (cart) {
            if (!sectionIds.every((id) => typeof result.sections?.[id] === 'string')) throw new Error();
            cart.classList.remove('is-empty');
            cart.renderContents(result);
          } else {
            const bubble = document.getElementById('cart-icon-bubble');
            const html = result.sections?.['cart-icon-bubble'];
            const content = typeof html === 'string'
              ? new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section') : null;
            if (!bubble || !content) throw new Error();
            bubble.innerHTML = content.innerHTML;
          }
          this.status.textContent = this.dataset.successMessage;
          this.status.hidden = false;
        } catch {
          window.location.assign(routes.cart_url);
        }
      } catch {
        this.status.textContent = errorMessage;
        this.status.hidden = false;
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-disabled');
        this.button.removeAttribute('aria-busy');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }
  });
}

if (!customElements.get('mk-sticky-product')) {
  customElements.define('mk-sticky-product', class extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('product-info');
      this.form = this.product?.querySelector(`#product-form-${this.dataset.section}`);
      this.sourceButton = this.form?.querySelector('[name="add"]');
      if (!this.sourceButton || !('IntersectionObserver' in window)) return;

      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-sticky-label]');
      this.price = this.querySelector('[data-sticky-price]');
      this.sourcePrice = this.product.querySelector(`#price-${this.dataset.section}`);
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.onResize = () => this.updateVisibility();
      this.onClick = () => {
        if (this.button.disabled) return;
        this.submittedFromBar = true;
        // Uses the real form's variant, quantity, properties and validation.
        this.form.requestSubmit(this.sourceButton);
      };
      this.button.addEventListener('click', this.onClick);
      this.mobile.addEventListener('change', this.onResize);

      this.mutations = new MutationObserver(() => this.sync());
      this.mutations.observe(this.sourceButton, {
        attributes: true, childList: true, subtree: true, characterData: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });
      if (this.sourcePrice) this.mutations.observe(this.sourcePrice, {
        attributes: true, childList: true, subtree: true, characterData: true,
        attributeFilter: ['class'],
      });
      this.sourceError = this.product.querySelector('.product-form__error-message-wrapper');
      if (this.sourceError) this.mutations.observe(this.sourceError, {
        attributes: true, childList: true, subtree: true, characterData: true,
        attributeFilter: ['hidden'],
      });
      this.intersection = new IntersectionObserver(([entry]) => {
        // A button below the fold has not yet been passed: no premature bar.
        this.pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.updateVisibility();
      });
      this.intersection.observe(this.sourceButton);
      this.sync();
    }

    sync() {
      this.button.disabled = this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';
      this.button.setAttribute('aria-busy', String(this.sourceButton.classList.contains('loading')));
      this.label.textContent = this.sourceButton.querySelector('span')?.textContent.trim() || '';
      if (this.sourcePrice) {
        // Price markup is server-rendered by Dawn and updates with its variants.
        this.price.innerHTML = this.sourcePrice.innerHTML;
        this.price.hidden = this.sourcePrice.classList.contains('hidden');
      }
      if (this.submittedFromBar && this.sourceError && !this.sourceError.hidden) {
        this.submittedFromBar = false;
        this.sourceError.scrollIntoView({ block: 'center' });
      }
      this.updateVisibility();
    }

    updateVisibility() {
      const visible = this.mobile.matches && this.pastButton && !this.sourceButton.disabled;
      this.hidden = !visible;
      document.documentElement.classList.toggle('mk-sticky-cart-active', Boolean(visible));
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      this.mobile?.removeEventListener('change', this.onResize);
      this.intersection?.disconnect();
      this.mutations?.disconnect();
      document.documentElement.classList.remove('mk-sticky-cart-active');
    }
  });
}
